import { WorkCalendar, addDays, diffDays, endOfMonth, maxDate, monthKey, type IsoDate } from './dates';
import { estimateLeadTime } from './leadTimes';
import { applyLotRules, evaluatePriceBreak, unitCostAt } from './lotSizing';
import type {
  CalendarEvent,
  CashBucket,
  CashSummary,
  DataGap,
  DemandDay,
  ItemPlan,
  ItemRole,
  LeadTimeEstimate,
  OpenSupply,
  PlanLine,
  PlanLineSchedule,
  PlannedOrder,
  PlannedOrderStatus,
  PlanningInputs,
  PlanningItem,
  PlanningModel,
  PlanningSettings,
  PlanningSummary,
  PlanningSupplier,
  ProjectedShortage,
  ResolvedSupply,
  TimelinePoint,
} from './types';

/**
 * Production planning engine.
 *
 * Classic time-phased MRP, run over the whole org in one pass:
 *
 *   1. Turn plan lines (batch / daily / monthly targets) into units to
 *      build per working day.
 *   2. Walk the BOM tree top-down in low-level-code order. Each level is
 *      netted against stock on hand and open purchase orders before its
 *      requirements are exploded to the next level, so sub-assemblies
 *      already on the shelf never generate component demand.
 *   3. For purchased parts, project the day-by-day balance, find the day
 *      it dips below safety stock, size an order that covers the next
 *      `orderCoverageDays` (rounded to MOQ / pack size) and work back
 *      from that day with the lead time to get the ideal and latest
 *      order dates.
 *   4. Produce calendar events, a monthly cash view and a list of the
 *      data gaps that stop any of the above being trustworthy.
 *
 * Pure: no React, no I/O. Everything is `YYYY-MM-DD` strings.
 */

const EPS = 1e-6;
/** How long after its expected date an unreceived PO is assumed to take. */
const OVERDUE_PO_GRACE_DAYS = 7;
/** Lead time assumed for a PO with no dates and a part with no lead time. */
const UNKNOWN_LEAD_TIME_DAYS = 14;
const ORDER_SOON_WINDOW_DAYS = 7;
const PLACED_PO_STATUSES = new Set(['ordered', 'in_production', 'shipped', 'partially_received']);

const STATUS_RANK: Record<PlannedOrderStatus, number> = {
  late: 0,
  order_now: 1,
  order_soon: 2,
  unknown_lead_time: 3,
  ok: 4,
};

export const PLANNED_ORDER_STATUS_LABELS: Record<PlannedOrderStatus, string> = {
  late: 'Late',
  order_now: 'Order now',
  order_soon: 'Order this week',
  unknown_lead_time: 'Lead time unknown',
  ok: 'On track',
};

export function runPlanning(inputs: PlanningInputs): PlanningModel {
  const { today, settings } = inputs;
  const calendar = new WorkCalendar(settings.workingDays, inputs.holidays);
  const horizonEnd = addDays(today, settings.horizonDays);
  const itemsById = new Map(inputs.items.map((i) => [i.id, i]));

  const gaps: DataGap[] = [];
  const gapIds = new Set<string>();
  const addGap = (gap: DataGap) => {
    if (gapIds.has(gap.id)) return;
    gapIds.add(gap.id);
    gaps.push(gap);
  };

  // ---- 1. Demand schedule -------------------------------------------------
  const schedules: PlanLineSchedule[] = [];
  const independent = new Map<string, Map<IsoDate, number>>();
  const independentItems = new Set<string>();
  const netStockItems = new Set<string>();
  const dailyProduction = new Map<IsoDate, Array<{ itemId: string; quantity: number }>>();

  for (const line of inputs.planLines) {
    if (!line.isActive) continue;
    const item = itemsById.get(line.itemId) ?? null;
    const { schedule, days } = schedulePlanLine(line, item, today, horizonEnd, calendar);
    schedules.push(schedule);
    if (!item) {
      addGap({
        id: `missing_item:${line.id}`,
        kind: 'missing_item',
        severity: 'blocking',
        itemId: null,
        title: 'Plan line points at an item that no longer exists',
        detail: 'Remove the target or point it at a current item.',
        fix: { type: 'plan_line', planLineId: line.id },
      });
      continue;
    }
    if (!item.isActive) {
      addGap({
        id: `inactive_item:${line.id}`,
        kind: 'inactive_item',
        severity: 'warning',
        itemId: item.id,
        title: `${item.name} is archived`,
        detail: 'The plan still builds it; reactivate the item or remove the target.',
        fix: { type: 'plan_line', planLineId: line.id },
      });
    }
    for (const issue of schedule.issues) {
      addGap({
        id: `plan_line_issue:${line.id}:${issue}`,
        kind: 'plan_line_issue',
        severity: 'warning',
        itemId: item.id,
        title: `${item.name}: ${issue}`,
        detail: 'Adjust the dates or quantity on this target.',
        fix: { type: 'plan_line', planLineId: line.id },
      });
    }
    if (days.length === 0) continue;

    independentItems.add(item.id);
    if (line.netAgainstStock) netStockItems.add(item.id);
    const byDate = independent.get(item.id) ?? new Map<IsoDate, number>();
    for (const day of days) {
      byDate.set(day.date, (byDate.get(day.date) ?? 0) + day.quantity);
      const dayList = dailyProduction.get(day.date) ?? [];
      const existing = dayList.find((d) => d.itemId === item.id);
      if (existing) existing.quantity += day.quantity;
      else dayList.push({ itemId: item.id, quantity: day.quantity });
      dailyProduction.set(day.date, dayList);
    }
    independent.set(item.id, byDate);
  }

  // ---- 2. BOM structure + low-level codes ----------------------------------
  const children = new Map<string, PlanningInputs['bomLines']>();
  const parents = new Map<string, PlanningInputs['bomLines']>();
  for (const line of inputs.bomLines) {
    (children.get(line.parentItemId) ?? children.set(line.parentItemId, []).get(line.parentItemId)!).push(line);
    (parents.get(line.componentItemId) ?? parents.set(line.componentItemId, []).get(line.componentItemId)!).push(line);
  }

  const lowLevel = new Map<string, number>();
  const cyclic = new Set<string>();
  const visit = (id: string, depth: number, lineage: string[]) => {
    if (lineage.includes(id)) {
      cyclic.add(id);
      const item = itemsById.get(id);
      addGap({
        id: `bom_cycle:${id}`,
        kind: 'bom_cycle',
        severity: 'blocking',
        itemId: id,
        title: `${item?.name ?? 'An assembly'} contains itself through its BOM`,
        detail: 'The explosion stops here. Fix the loop in the BOM builder.',
        fix: { type: 'item', itemId: id, tab: 'bom' },
      });
      return;
    }
    if ((lowLevel.get(id) ?? -1) >= depth) return;
    lowLevel.set(id, depth);
    for (const child of children.get(id) ?? []) {
      visit(child.componentItemId, depth + 1, [...lineage, id]);
    }
  };
  for (const id of independentItems) visit(id, 0, []);
  const involved = [...lowLevel.entries()].sort((a, b) => a[1] - b[1] || a[0].localeCompare(b[0]));

  // ---- 3. Lookups ----------------------------------------------------------
  const suppliersByItem = groupBy(inputs.suppliers, (s) => s.itemId);
  const samplesByItem = groupBy(inputs.leadTimeSamples, (s) => s.itemId);
  const supplyByItem = groupBy(
    inputs.openSupply.filter((s) => s.remainingQty > 0 && (s.status === 'internal_po' || PLACED_PO_STATUSES.has(s.status))),
    (s) => s.itemId,
  );
  const onHandByItem = new Map<string, number>();
  for (const rec of inputs.onHand) {
    if (rec.locationKind === 'quarantine') continue;
    onHandByItem.set(rec.itemId, (onHandByItem.get(rec.itemId) ?? 0) + rec.quantity);
  }

  const dates: IsoDate[] = [];
  for (let d = today; d <= horizonEnd; d = addDays(d, 1)) dates.push(d);
  const dateIndex = new Map(dates.map((d, i) => [d, i] as const));
  const workingDayCount = Math.max(1, dates.filter((d) => calendar.isWorkingDay(d)).length);
  const zeros = () => new Array<number>(dates.length).fill(0);

  // ---- 4. Netting, level by level ------------------------------------------
  const gross = new Map<string, number[]>();
  const dependent = new Map<string, number[]>();
  const arr = (map: Map<string, number[]>, id: string) => map.get(id) ?? map.set(id, zeros()).get(id)!;
  for (const [id, byDate] of independent) {
    const target = arr(gross, id);
    for (const [date, qty] of byDate) {
      const i = dateIndex.get(date);
      if (i != null) target[i] += qty;
    }
  }

  const ctx: ItemContext = {
    today,
    horizonEnd,
    settings,
    calendar,
    dates,
    dateIndex,
    workingDayCount,
    zeros,
    itemsById,
    addGap,
  };

  const itemPlans: ItemPlan[] = [];
  for (const [itemId, level] of involved) {
    const item = itemsById.get(itemId);
    if (!item) continue;
    const grossArr = gross.get(itemId) ?? zeros();
    const depArr = dependent.get(itemId) ?? zeros();
    const kids = children.get(itemId) ?? [];
    const supplierList = suppliersByItem.get(itemId) ?? [];
    const supplier = pickSupplier(supplierList);
    const isFinishedKind = [item.kind, ...item.additionalKinds].includes('finished_good');
    const hasIndependent = independentItems.has(itemId);

    let role: ItemRole;
    if (kids.length > 0 && !cyclic.has(itemId)) {
      role = isFinishedKind || hasIndependent ? 'finished_good' : 'sub_assembly';
    } else if (supplier) {
      role = 'purchased';
    } else if (isFinishedKind || hasIndependent) {
      role = 'finished_good';
    } else {
      role = 'purchased';
    }

    const plan = planItem(ctx, {
      item,
      role,
      level,
      grossArr,
      depArr,
      supplier,
      samples: samplesByItem.get(itemId) ?? [],
      supplies: supplyByItem.get(itemId) ?? [],
      onHand: onHandByItem.get(itemId) ?? 0,
      ignoreStock: role === 'finished_good' && hasIndependent && !netStockItems.has(itemId),
      hasBom: kids.length > 0,
      usedIn: (parents.get(itemId) ?? []).map((l) => ({
        itemId: l.parentItemId,
        quantityPer: l.quantity * (1 + (l.scrapPct || 0) / 100),
      })),
    });
    itemPlans.push(plan);

    if (role !== 'purchased' && kids.length > 0 && !cyclic.has(itemId)) {
      for (const build of plan.plannedProduction) {
        const i = dateIndex.get(build.date);
        if (i == null) continue;
        for (const child of kids) {
          const per = child.quantity * (1 + (child.scrapPct || 0) / 100);
          arr(gross, child.componentItemId)[i] += build.quantity * per;
          arr(dependent, child.componentItemId)[i] += build.quantity * per;
        }
      }
    }
  }

  for (const plan of itemPlans) plan.gaps.forEach(addGap);

  // ---- 5. Rank, events, cash, summary --------------------------------------
  const purchased = itemPlans.filter((p) => p.role === 'purchased').sort(compareUrgency);
  const orders = itemPlans
    .flatMap((p) => p.orders)
    .sort(
      (a, b) =>
        STATUS_RANK[a.status] - STATUS_RANK[b.status] ||
        (a.idealOrderDate ?? a.needByDate).localeCompare(b.idealOrderDate ?? b.needByDate) ||
        a.needByDate.localeCompare(b.needByDate),
    );
  const itemPlanById = new Map(itemPlans.map((p) => [p.item.id, p]));

  const events = buildEvents(ctx, itemPlans, purchased, orders, dailyProduction, inputs.holidays);
  const cash = buildCash(ctx, itemPlans, purchased, orders);
  const summary = buildSummary(schedules, itemPlans, purchased, orders, gaps);

  return {
    today,
    horizonEnd,
    calendar,
    settings,
    schedules,
    dailyProduction,
    items: itemPlans,
    itemsById: itemPlanById,
    purchased,
    orders,
    events,
    gaps: [...gaps].sort(compareGaps),
    cash,
    summary,
  };
}

// ---------------------------------------------------------------------------
// Plan lines → units per working day
// ---------------------------------------------------------------------------

export function schedulePlanLine(
  line: PlanLine,
  item: PlanningItem | null,
  today: IsoDate,
  horizonEnd: IsoDate,
  calendar: WorkCalendar,
): { schedule: PlanLineSchedule; days: DemandDay[] } {
  const issues: string[] = [];
  let candidateDays: IsoDate[] = [];
  let perDay = 0;

  if (line.kind === 'daily') {
    const start = maxDate(line.startDate ?? today, today);
    const end = line.endDate ?? horizonEnd;
    if (end < today) issues.push('the daily target ended in the past');
    candidateDays = calendar.workingDaysBetween(start, end);
    perDay = line.quantity;
  } else if (line.kind === 'batch') {
    const due = line.endDate ?? today;
    const start = maxDate(line.startDate ?? today, today);
    if (due < today) {
      issues.push('the due date has passed');
      candidateDays = [today];
    } else {
      candidateDays = calendar.workingDaysBetween(start, due);
      if (candidateDays.length === 0) candidateDays = [due];
    }
    perDay = line.quantity / candidateDays.length;
  } else {
    const anchor = line.startDate ?? today;
    if (endOfMonth(anchor) < today) {
      issues.push('the month has already ended');
    } else {
      const monthDays = calendar.workingDaysInMonth(anchor);
      const remaining = monthDays.filter((d) => d >= today);
      if (remaining.length === 0) {
        issues.push('no working days left in that month');
      } else {
        candidateDays = remaining;
        perDay = line.quantity / remaining.length;
      }
    }
  }

  const inHorizon = candidateDays.filter((d) => d <= horizonEnd);
  const beyond = candidateDays.length - inHorizon.length;
  const days = inHorizon.map((date) => ({ date, quantity: perDay }));

  return {
    schedule: {
      line,
      item,
      firstDate: candidateDays[0] ?? null,
      lastDate: candidateDays[candidateDays.length - 1] ?? null,
      workingDays: candidateDays.length,
      perDay,
      totalInHorizon: perDay * inHorizon.length,
      totalBeyondHorizon: perDay * beyond,
      issues,
    },
    days,
  };
}

// ---------------------------------------------------------------------------
// One item
// ---------------------------------------------------------------------------

interface ItemContext {
  today: IsoDate;
  horizonEnd: IsoDate;
  settings: PlanningSettings;
  calendar: WorkCalendar;
  dates: IsoDate[];
  dateIndex: Map<IsoDate, number>;
  workingDayCount: number;
  zeros: () => number[];
  itemsById: Map<string, PlanningItem>;
  addGap: (gap: DataGap) => void;
}

interface ItemArgs {
  item: PlanningItem;
  role: ItemRole;
  level: number;
  grossArr: number[];
  depArr: number[];
  supplier: PlanningSupplier | null;
  samples: PlanningInputs['leadTimeSamples'];
  supplies: OpenSupply[];
  onHand: number;
  /** Finished goods with build targets: the target is units to build, not units to have. */
  ignoreStock: boolean;
  hasBom: boolean;
  usedIn: ItemPlan['usedIn'];
}

function planItem(ctx: ItemContext, args: ItemArgs): ItemPlan {
  const { item, role, supplier, grossArr, depArr } = args;
  const { settings, dates } = ctx;
  const gaps: DataGap[] = [];
  const n = dates.length;

  const grossRequirement = sum(grossArr);
  const dependentDemand = sum(depArr);
  const independentDemand = grossRequirement - dependentDemand;
  const hasDemand = grossRequirement > EPS;
  const avgDailyUsage = grossRequirement / ctx.workingDayCount;
  const safetyStockQty = role === 'purchased' ? avgDailyUsage * settings.safetyStockDays : 0;

  const leadTime = estimateLeadTime(item, supplier, args.samples);
  const typical = leadTime.typicalDays;
  const unitCostCents = unitCostAt(1, supplier, item.stdCostCents);

  // -- Open supply ----------------------------------------------------------
  const placedArr = ctx.zeros();
  const draftArr = ctx.zeros();
  const supplies: ResolvedSupply[] = [];
  let onOrderPlaced = 0;
  let onOrderDraft = 0;
  for (const raw of args.supplies) {
    const resolved = resolveSupply(ctx, raw, typical, gaps, item);
    supplies.push(resolved);
    if (resolved.isDraft) onOrderDraft += raw.remainingQty;
    else onOrderPlaced += raw.remainingQty;
    const i = ctx.dateIndex.get(resolved.arrivalDate);
    if (i == null) continue; // past the horizon — does not help the plan
    (resolved.isDraft ? draftArr : placedArr)[i] += raw.remainingQty;
  }
  supplies.sort((a, b) => a.arrivalDate.localeCompare(b.arrivalDate));

  // -- Projected balances ---------------------------------------------------
  const startBalance = args.ignoreStock ? 0 : args.onHand;
  const balancePlaced = ctx.zeros();
  const balanceAll = ctx.zeros();
  let runoutDate: IsoDate | null = null;
  let runoutDateWithDrafts: IsoDate | null = null;
  let coverageEndsDate: IsoDate | null = null;
  let bp = startBalance;
  let ba = startBalance;
  for (let i = 0; i < n; i++) {
    bp += placedArr[i] - grossArr[i];
    ba += placedArr[i] + draftArr[i] - grossArr[i];
    balancePlaced[i] = bp;
    balanceAll[i] = ba;
    if (runoutDate == null && bp < -EPS) runoutDate = dates[i];
    if (runoutDateWithDrafts == null && ba < -EPS) runoutDateWithDrafts = dates[i];
    if (coverageEndsDate == null && ba < safetyStockQty - EPS) coverageEndsDate = dates[i];
  }

  // -- Suggestions ------------------------------------------------------------
  const pab = [...balanceAll];
  const orders: PlannedOrder[] = [];
  const shortages: ProjectedShortage[] = [];
  const plannedProduction: DemandDay[] = [];

  if (role === 'purchased') {
    // Earliest day anything ordered today can be on the shelf.
    const earliestArrival =
      typical != null ? addDays(ctx.today, typical + settings.receivingBufferDays) : null;
    const earliestIdx = earliestArrival == null ? 0 : (ctx.dateIndex.get(earliestArrival) ?? n);

    let i = 0;
    while (i < n) {
      if (pab[i] >= safetyStockQty - EPS) {
        i++;
        continue;
      }
      const needBy = dates[i];
      let coversFromIdx = i;

      if (i < earliestIdx) {
        // Stock dips before any new order can land: that stretch is a
        // shortage no PO fixes. Record it, then size the order for the
        // day the parts could actually arrive.
        const shortage = measureShortage(ctx, pab, i, Math.min(earliestIdx, n), earliestArrival);
        if (shortage) shortages.push(shortage);
        if (earliestIdx >= n) break;
        coversFromIdx = earliestIdx;
        if (pab[coversFromIdx] >= safetyStockQty - EPS) {
          i = coversFromIdx + 1;
          continue;
        }
      }

      const endIdx = Math.min(n - 1, coversFromIdx + settings.orderCoverageDays);
      let minPab = pab[coversFromIdx];
      for (let j = coversFromIdx; j <= endIdx; j++) if (pab[j] < minPab) minPab = pab[j];
      const netRequirement = safetyStockQty - minPab;
      const lot = applyLotRules(netRequirement, supplier);
      if (lot.quantity <= 0) {
        i = coversFromIdx + 1;
        continue;
      }
      for (let j = coversFromIdx; j < n; j++) pab[j] += lot.quantity;

      orders.push(
        buildOrder(ctx, {
          item,
          supplier,
          leadTime,
          needBy,
          coversFrom: dates[coversFromIdx],
          coverageEnd: dates[endIdx],
          netRequirement,
          lot,
          avgDailyUsage,
          sequence: orders.length,
        }),
      );
      i = coversFromIdx + 1;
    }
  } else if (args.hasBom) {
    for (let i = 0; i < n; i++) {
      if (pab[i] >= -EPS) continue;
      const qty = -pab[i];
      plannedProduction.push({ date: dates[i], quantity: qty });
      for (let j = i; j < n; j++) pab[j] += qty;
    }
  }

  const timeline: TimelinePoint[] = dates.map((date, i) => ({
    date,
    demand: grossArr[i],
    receipts: placedArr[i] + draftArr[i],
    balance: balancePlaced[i],
    plannedBalance: pab[i],
  }));

  // -- Data gaps ----------------------------------------------------------------
  if (hasDemand) {
    if (role !== 'purchased' && !args.hasBom) {
      gaps.push({
        id: `missing_bom:${item.id}`,
        kind: 'missing_bom',
        severity: 'blocking',
        itemId: item.id,
        title: `${item.name} has no bill of materials`,
        detail: `${fmtQty(grossRequirement)} planned but there is nothing to explode into parts. Build the BOM, or add a supplier if it is bought in.`,
        fix: { type: 'item', itemId: item.id, tab: 'bom' },
      });
    }
    if (role === 'purchased') {
      if (!supplier) {
        gaps.push({
          id: `missing_supplier:${item.id}`,
          kind: 'missing_supplier',
          severity: 'blocking',
          itemId: item.id,
          title: `${item.name} has no supplier`,
          detail: `The plan needs ${fmtQty(grossRequirement)}. Without a vendor there is no lead time, MOQ or price, and no PO can be drafted.`,
          fix: { type: 'item', itemId: item.id, tab: 'suppliers' },
        });
      }
      if (leadTime.worstDays == null) {
        gaps.push({
          id: `missing_lead_time:${item.id}`,
          kind: 'missing_lead_time',
          severity: 'blocking',
          itemId: item.id,
          title: `${item.name} has no lead time`,
          detail: supplier
            ? `Add a lead time on the ${supplier.vendorName} supplier record (or the item's worst-case lead time) so order dates can be computed.`
            : 'Add a supplier with a lead time, or a worst-case lead time on the item.',
          fix: { type: 'item', itemId: item.id, tab: supplier ? 'suppliers' : 'details' },
        });
      } else if (leadTime.source !== 'observed') {
        gaps.push({
          id: `unverified_lead_time:${item.id}`,
          kind: 'unverified_lead_time',
          severity: 'info',
          itemId: item.id,
          title: `${item.name}: lead time is ${leadTime.source === 'quoted' ? 'the vendor quote' : 'from the catalog'} only`,
          detail: `${leadTime.typicalDays} days has not been confirmed by a receipt yet. It will tighten up automatically once POs for this part are received.`,
          fix: null,
        });
      }
      if (unitCostCents == null) {
        gaps.push({
          id: `missing_cost:${item.id}`,
          kind: 'missing_cost',
          severity: 'warning',
          itemId: item.id,
          title: `${item.name} has no unit cost`,
          detail: 'Its orders are left out of the cash projection. Add a supplier price or a standard cost.',
          fix: { type: 'item', itemId: item.id, tab: supplier ? 'suppliers' : 'details' },
        });
      }
      if (supplier && supplier.minOrderQty == null && orders.length > 0) {
        gaps.push({
          id: `missing_moq:${item.id}`,
          kind: 'missing_moq',
          severity: 'info',
          itemId: item.id,
          title: `${item.name}: no MOQ on file for ${supplier.vendorName}`,
          detail: 'Suggested quantities may be below what the vendor will accept.',
          fix: { type: 'item', itemId: item.id, tab: 'suppliers' },
        });
      }
    }
  }

  return {
    item,
    role,
    lowLevelCode: args.level,
    onHand: args.onHand,
    safetyStockQty,
    onOrderPlaced,
    onOrderDraft,
    grossRequirement,
    independentDemand,
    dependentDemand,
    avgDailyUsage,
    daysOfCover: avgDailyUsage > EPS ? args.onHand / avgDailyUsage : null,
    runoutDate,
    runoutDateWithDrafts,
    coverageEndsDate,
    leadTime,
    supplier,
    unitCostCents,
    orders,
    shortages,
    plannedProduction,
    supplies,
    usedIn: args.usedIn,
    gaps,
    timeline,
  };
}

/** Working days in `[from, to)` where the balance is negative, if any. */
function measureShortage(
  ctx: ItemContext,
  pab: number[],
  from: number,
  to: number,
  earliestArrival: IsoDate | null,
): ProjectedShortage | null {
  let first: IsoDate | null = null;
  let last: IsoDate | null = null;
  let workingDays = 0;
  let maxShortQty = 0;
  for (let j = from; j < to; j++) {
    if (pab[j] >= -EPS) continue;
    first ??= ctx.dates[j];
    last = ctx.dates[j];
    if (ctx.calendar.isWorkingDay(ctx.dates[j])) workingDays += 1;
    maxShortQty = Math.max(maxShortQty, -pab[j]);
  }
  if (!first || !last) return null;
  return { fromDate: first, toDate: last, workingDays, maxShortQty, earliestArrival };
}

function resolveSupply(
  ctx: ItemContext,
  supply: OpenSupply,
  typicalLead: number | null,
  gaps: DataGap[],
  item: PlanningItem,
): ResolvedSupply {
  const { today, settings } = ctx;
  const isDraft = supply.status === 'internal_po';
  const lead = supply.leadTimeDays ?? typicalLead ?? UNKNOWN_LEAD_TIME_DAYS;
  let arrival: IsoDate;
  let assumed = false;
  let overdue = false;

  if (isDraft) {
    let place = supply.orderByDate ?? today;
    if (place < today) {
      place = today;
      overdue = true;
      gaps.push({
        id: `draft_po_overdue:${supply.poId}`,
        kind: 'draft_po_overdue',
        severity: 'warning',
        itemId: null,
        title: `${supply.poNumber} should have been placed by ${supply.orderByDate}`,
        detail: `Draft for ${supply.vendorName ?? 'the vendor'} is still unplaced. Place it today or the parts on it arrive late.`,
        fix: { type: 'po', poId: supply.poId },
      });
    }
    arrival = addDays(place, lead);
    assumed = true;
  } else if (supply.expectedAt) {
    arrival = supply.expectedAt;
  } else if (supply.orderedAt) {
    arrival = addDays(supply.orderedAt, lead);
    assumed = true;
    gaps.push({
      id: `missing_expected_date:${supply.poId}`,
      kind: 'missing_expected_date',
      severity: 'info',
      itemId: null,
      title: `${supply.poNumber} has no expected delivery date`,
      detail: `Assuming ${lead} days from the order date. Add the vendor's promised date for an accurate calendar.`,
      fix: { type: 'po', poId: supply.poId },
    });
  } else {
    arrival = addDays(today, lead);
    assumed = true;
    gaps.push({
      id: `missing_expected_date:${supply.poId}`,
      kind: 'missing_expected_date',
      severity: 'warning',
      itemId: null,
      title: `${supply.poNumber} has no order or expected date`,
      detail: `Assuming the parts land in ${lead} days. Fill in the dates on the PO.`,
      fix: { type: 'po', poId: supply.poId },
    });
  }

  if (!isDraft && arrival < today) {
    overdue = true;
    arrival = addDays(today, OVERDUE_PO_GRACE_DAYS);
    assumed = true;
    gaps.push({
      id: `overdue_po:${supply.poId}`,
      kind: 'overdue_po',
      severity: 'warning',
      itemId: null,
      title: `${supply.poNumber} is overdue`,
      detail: `Expected ${supply.expectedAt ?? 'earlier'} and not received (${item.name} and possibly more). Chase the vendor or update the expected date; the plan assumes it lands in ${OVERDUE_PO_GRACE_DAYS} days.`,
      fix: { type: 'po', poId: supply.poId },
    });
  }

  return {
    ...supply,
    arrivalDate: addDays(arrival, settings.receivingBufferDays),
    isDraft,
    isOverdue: overdue,
    arrivalAssumed: assumed,
  };
}

interface OrderArgs {
  item: PlanningItem;
  supplier: PlanningSupplier | null;
  leadTime: LeadTimeEstimate;
  needBy: IsoDate;
  coversFrom: IsoDate;
  coverageEnd: IsoDate;
  netRequirement: number;
  lot: ReturnType<typeof applyLotRules>;
  avgDailyUsage: number;
  sequence: number;
}

function buildOrder(ctx: ItemContext, args: OrderArgs): PlannedOrder {
  const { settings, calendar, today } = ctx;
  const { item, supplier, leadTime, needBy, lot } = args;
  const typical = leadTime.typicalDays;
  const worst = leadTime.worstDays;
  const receiving = settings.receivingBufferDays;

  const unitCostCents = unitCostAt(lot.quantity, supplier, item.stdCostCents);
  const priceBreakOption = evaluatePriceBreak(lot.quantity, supplier, item.stdCostCents, args.avgDailyUsage);

  let idealOrderDate: IsoDate | null = null;
  let latestOrderDate: IsoDate | null = null;
  let status: PlannedOrderStatus = 'unknown_lead_time';
  if (worst != null && typical != null) {
    idealOrderDate = calendar.previousWorkingDay(
      addDays(needBy, -(worst + settings.safetyBufferDays + receiving)),
    );
    latestOrderDate = calendar.previousWorkingDay(addDays(needBy, -(typical + receiving)));
    if (latestOrderDate < idealOrderDate) latestOrderDate = idealOrderDate;
    if (args.coversFrom > needBy) status = 'late';
    else if (idealOrderDate < today) status = 'order_now';
    else if (diffDays(today, idealOrderDate) <= ORDER_SOON_WINDOW_DAYS) status = 'order_soon';
    else status = 'ok';
  }

  const arrivalIfOrderedToday = typical != null ? addDays(today, typical + receiving) : null;
  const expectedArrivalDate =
    typical != null && idealOrderDate != null
      ? addDays(maxDate(idealOrderDate, today), typical + receiving)
      : null;

  return {
    id: `${item.id}:${args.sequence}`,
    itemId: item.id,
    supplier,
    needByDate: needBy,
    coversFromDate: args.coversFrom,
    coverageEndDate: args.coverageEnd,
    netRequirement: args.netRequirement,
    quantity: lot.quantity,
    purchaseQuantity: lot.purchaseQuantity,
    moqApplied: lot.moqApplied,
    multipleApplied: lot.multipleApplied,
    unitCostCents,
    totalCents: unitCostCents != null ? Math.round(unitCostCents * lot.quantity) : null,
    priceBreakOption,
    idealOrderDate,
    latestOrderDate,
    expectedArrivalDate,
    arrivalIfOrderedToday,
    typicalLeadDays: typical,
    worstLeadDays: worst,
    status,
  };
}

// ---------------------------------------------------------------------------
// Calendar events
// ---------------------------------------------------------------------------

function buildEvents(
  ctx: ItemContext,
  items: ItemPlan[],
  purchased: ItemPlan[],
  orders: PlannedOrder[],
  dailyProduction: Map<IsoDate, Array<{ itemId: string; quantity: number }>>,
  holidays: IsoDate[],
): CalendarEvent[] {
  const { today, horizonEnd, itemsById } = ctx;
  const events: CalendarEvent[] = [];
  const name = (id: string) => itemsById.get(id)?.name ?? 'Item';
  const planById = new Map(items.map((p) => [p.item.id, p]));

  for (const order of orders) {
    const item = itemsById.get(order.itemId);
    const vendor = order.supplier?.vendorName ?? null;
    const purchaseUom = order.supplier?.purchaseUom;
    const qty =
      purchaseUom && order.purchaseQuantity !== order.quantity
        ? `${fmtQty(order.purchaseQuantity)} ${purchaseUom} (${fmtQty(order.quantity)} ${item?.baseUom ?? ''})`
        : `${fmtQty(order.quantity)} ${item?.baseUom ?? ''}`.trim();
    const base = {
      itemId: order.itemId,
      poId: null,
      orderId: order.id,
      vendorId: order.supplier?.vendorId ?? null,
      quantity: order.quantity,
      amountCents: order.totalCents,
    };

    if (order.status === 'late') {
      const shortage = planById.get(order.itemId)?.shortages[0];
      events.push({
        ...base,
        id: `late:${order.id}`,
        date: today,
        kind: 'order_latest',
        severity: 'critical',
        title: `Late: order ${qty} ${name(order.itemId)}`,
        detail: [
          `Should have gone out by ${order.latestOrderDate}.`,
          `Ordered today it lands ${order.coversFromDate}`,
          shortage
            ? `— short ${fmtQty(shortage.maxShortQty)} for ${shortage.workingDays} working day${
                shortage.workingDays === 1 ? '' : 's'
              } (${shortage.fromDate} → ${shortage.toDate}).`
            : '— safety stock absorbs the gap.',
          vendor ? `Vendor: ${vendor}.` : null,
        ]
          .filter(Boolean)
          .join(' '),
      });
    } else if (order.status === 'order_now') {
      events.push({
        ...base,
        id: `now:${order.id}`,
        date: today,
        kind: 'order_ideal',
        severity: 'warning',
        title: `Order now: ${qty} ${name(order.itemId)}`,
        detail: `Ideal date was ${order.idealOrderDate}; last safe day ${order.latestOrderDate}.${vendor ? ` Vendor: ${vendor}.` : ''}`,
      });
      if (order.latestOrderDate && order.latestOrderDate > today) {
        events.push({
          ...base,
          id: `latest:${order.id}`,
          date: order.latestOrderDate,
          kind: 'order_latest',
          severity: 'warning',
          title: `Last day to order ${name(order.itemId)}`,
          detail: `Needed ${order.needByDate}; typical lead time ${ctxLead(order)}.`,
        });
      }
    } else if (order.idealOrderDate) {
      events.push({
        ...base,
        id: `ideal:${order.id}`,
        date: order.idealOrderDate,
        kind: 'order_ideal',
        severity: order.status === 'order_soon' ? 'warning' : 'info',
        title: `Order ${qty} ${name(order.itemId)}`,
        detail: `Needed ${order.needByDate}; covers through ${order.coverageEndDate}.${vendor ? ` Vendor: ${vendor}.` : ''}`,
      });
      if (order.latestOrderDate && order.latestOrderDate !== order.idealOrderDate) {
        events.push({
          ...base,
          id: `latest:${order.id}`,
          date: order.latestOrderDate,
          kind: 'order_latest',
          severity: 'info',
          title: `Last day to order ${name(order.itemId)}`,
          detail: `Needed ${order.needByDate}; typical lead time ${ctxLead(order)}.`,
        });
      }
    }

    if (order.expectedArrivalDate && order.expectedArrivalDate <= horizonEnd) {
      events.push({
        ...base,
        id: `planned_receipt:${order.id}`,
        date: order.expectedArrivalDate,
        kind: 'planned_receipt',
        severity: 'info',
        title: `${qty} ${name(order.itemId)} lands (if ordered on time)`,
        detail: vendor ? `From ${vendor}.` : null,
      });
    }
  }

  for (const plan of purchased) {
    if (!plan.runoutDate) continue;
    const shortage = plan.shortages[0];
    const consequence = shortage
      ? `Nothing ordered now lands before ${shortage.earliestArrival ?? 'later'}: ${fmtQty(
          shortage.maxShortQty,
        )} short over ${shortage.workingDays} working day${shortage.workingDays === 1 ? '' : 's'} (${
          shortage.fromDate
        } → ${shortage.toDate}).`
      : plan.orders.length > 0
        ? 'Order on the suggested dates to avoid it.'
        : 'Production that needs it stops here unless more arrives.';
    events.push({
      id: `runout:${plan.item.id}`,
      date: plan.runoutDate,
      kind: 'runout',
      severity: 'critical',
      title: `${plan.item.name} runs out`,
      detail: `${fmtQty(plan.onHand)} on hand, using ${fmtQty(plan.avgDailyUsage)}/day${
        plan.onOrderPlaced > 0 ? `, ${fmtQty(plan.onOrderPlaced)} on open POs` : ''
      }. ${consequence}`,
      itemId: plan.item.id,
      poId: null,
      orderId: null,
      vendorId: plan.supplier?.vendorId ?? null,
      quantity: null,
      amountCents: null,
    });
  }

  const poGroups = new Map<string, { supply: ResolvedSupply; lines: number; qty: number; names: string[] }>();
  for (const plan of items) {
    for (const supply of plan.supplies) {
      const key = `${supply.poId}:${supply.isDraft ? 'draft' : supply.arrivalDate}`;
      const group = poGroups.get(key) ?? { supply, lines: 0, qty: 0, names: [] };
      group.lines += 1;
      group.qty += supply.remainingQty;
      group.names.push(plan.item.name);
      poGroups.set(key, group);
    }
  }
  for (const group of poGroups.values()) {
    const { supply } = group;
    const vendor = supply.vendorName ?? 'vendor';
    const lineText = `${group.lines} line${group.lines === 1 ? '' : 's'}: ${group.names.slice(0, 3).join(', ')}${
      group.names.length > 3 ? ` +${group.names.length - 3}` : ''
    }`;
    if (supply.isDraft) {
      const due = supply.orderByDate;
      const past = due != null && due < today;
      events.push({
        id: `po_order_by:${supply.poId}`,
        date: past || !due ? today : due,
        kind: 'po_order_by',
        severity: past ? 'critical' : 'warning',
        title: `${past ? 'Overdue: place' : 'Place'} ${supply.poNumber} with ${vendor}`,
        detail: `${lineText}. ${due ? `Order by ${due}.` : 'No order-by date set.'} Expected ${supply.arrivalDate} once placed.`,
        itemId: null,
        poId: supply.poId,
        orderId: null,
        vendorId: supply.vendorId,
        quantity: group.qty,
        amountCents: null,
      });
    } else if (supply.arrivalDate <= horizonEnd) {
      events.push({
        id: `receipt:${supply.poId}:${supply.arrivalDate}`,
        date: supply.arrivalDate,
        kind: 'receipt_expected',
        severity: supply.isOverdue ? 'warning' : 'info',
        title: `${supply.poNumber} from ${vendor} ${supply.isOverdue ? '(overdue)' : 'arrives'}`,
        detail: `${lineText}.${supply.arrivalAssumed ? ' Date is an estimate.' : ''}`,
        itemId: null,
        poId: supply.poId,
        orderId: null,
        vendorId: supply.vendorId,
        quantity: group.qty,
        amountCents: null,
      });
    }
  }

  for (const [date, builds] of dailyProduction) {
    const parts = builds
      .filter((b) => b.quantity > EPS)
      .map((b) => `${fmtQty(b.quantity)} ${name(b.itemId)}`);
    if (parts.length === 0) continue;
    events.push({
      id: `production:${date}`,
      date,
      kind: 'production',
      severity: 'info',
      title: `Build ${parts.join(', ')}`,
      detail: null,
      itemId: builds.length === 1 ? builds[0].itemId : null,
      poId: null,
      orderId: null,
      vendorId: null,
      quantity: builds.reduce((acc, b) => acc + b.quantity, 0),
      amountCents: null,
    });
  }

  for (const holiday of holidays) {
    const date = holiday.slice(0, 10);
    if (date < today || date > horizonEnd) continue;
    events.push({
      id: `holiday:${date}`,
      date,
      kind: 'holiday',
      severity: 'info',
      title: 'Holiday — no production',
      detail: null,
      itemId: null,
      poId: null,
      orderId: null,
      vendorId: null,
      quantity: null,
      amountCents: null,
    });
  }

  const severityRank = { critical: 0, warning: 1, info: 2 } as const;
  return events.sort(
    (a, b) =>
      a.date.localeCompare(b.date) ||
      severityRank[a.severity] - severityRank[b.severity] ||
      a.title.localeCompare(b.title),
  );
}

function ctxLead(order: PlannedOrder): string {
  return order.typicalLeadDays != null ? `${order.typicalLeadDays} days` : 'on file';
}

// ---------------------------------------------------------------------------
// Cash
// ---------------------------------------------------------------------------

function buildCash(ctx: ItemContext, items: ItemPlan[], purchased: ItemPlan[], orders: PlannedOrder[]): CashSummary {
  const { today, horizonEnd, settings } = ctx;
  const buckets = new Map<string, CashBucket>();
  for (let m = monthKey(today); m <= monthKey(horizonEnd); m = monthKey(addDays(endOfMonth(`${m}-01`), 1))) {
    buckets.set(m, {
      key: m,
      plannedCents: 0,
      draftCents: 0,
      committedCents: 0,
      budgetCents: settings.monthlyPurchasingBudgetCents,
      overBudgetCents: 0,
      orderIds: [],
    });
  }
  const bucketFor = (date: IsoDate) => buckets.get(monthKey(maxDate(date, today)));

  let plannedCents = 0;
  let ordersWithoutCost = 0;
  for (const order of orders) {
    const bucket = bucketFor(order.idealOrderDate ?? today);
    if (!bucket) continue;
    bucket.orderIds.push(order.id);
    if (order.totalCents == null) {
      ordersWithoutCost += 1;
      continue;
    }
    bucket.plannedCents += order.totalCents;
    plannedCents += order.totalCents;
  }

  let draftCents = 0;
  let committedCents = 0;
  const seenLines = new Set<string>();
  for (const plan of items) {
    for (const supply of plan.supplies) {
      if (seenLines.has(supply.poLineId)) continue;
      seenLines.add(supply.poLineId);
      const value = Math.round(supply.remainingQty * supply.unitPriceCents);
      if (supply.isDraft) {
        const bucket = bucketFor(supply.orderByDate ?? today);
        if (bucket) bucket.draftCents += value;
        draftCents += value;
      } else {
        const bucket = bucketFor(supply.arrivalDate);
        if (bucket) bucket.committedCents += value;
        committedCents += value;
      }
    }
  }

  let overBudgetMonths = 0;
  for (const bucket of buckets.values()) {
    if (bucket.budgetCents != null) {
      bucket.overBudgetCents = Math.max(0, bucket.plannedCents + bucket.draftCents - bucket.budgetCents);
      if (bucket.overBudgetCents > 0) overBudgetMonths += 1;
    }
  }

  const planMaterialCents = Math.round(
    purchased.reduce(
      (acc, p) => acc + (p.unitCostCents != null ? p.grossRequirement * p.unitCostCents : 0),
      0,
    ),
  );

  return {
    plannedCents,
    draftCents,
    committedCents,
    planMaterialCents,
    ordersWithoutCost,
    buckets: [...buckets.values()],
    overBudgetMonths,
  };
}

// ---------------------------------------------------------------------------
// Summary + ordering helpers
// ---------------------------------------------------------------------------

function buildSummary(
  schedules: PlanLineSchedule[],
  items: ItemPlan[],
  purchased: ItemPlan[],
  orders: PlannedOrder[],
  gaps: DataGap[],
): PlanningSummary {
  let nextRunout: PlanningSummary['nextRunout'] = null;
  for (const plan of purchased) {
    if (plan.runoutDate && (!nextRunout || plan.runoutDate < nextRunout.date)) {
      nextRunout = { itemId: plan.item.id, date: plan.runoutDate };
    }
  }
  return {
    unitsPlanned: schedules.reduce((acc, s) => acc + s.totalInHorizon, 0),
    finishedGoods: items.filter((p) => p.role === 'finished_good').length,
    purchasedParts: purchased.length,
    partsShort: purchased.filter((p) => p.runoutDate != null).length,
    partsWithUnavoidableShortage: purchased.filter((p) => p.shortages.length > 0).length,
    ordersLate: orders.filter((o) => o.status === 'late').length,
    ordersDueNow: orders.filter((o) => o.status === 'order_now').length,
    ordersDueSoon: orders.filter((o) => o.status === 'order_soon').length,
    suggestedOrders: orders.length,
    nextRunout,
    blockingGaps: gaps.filter((g) => g.severity === 'blocking').length,
    warningGaps: gaps.filter((g) => g.severity === 'warning').length,
  };
}

function urgencyRank(plan: ItemPlan): number {
  if (plan.orders.length === 0) return 9;
  return Math.min(...plan.orders.map((o) => STATUS_RANK[o.status]));
}

function compareUrgency(a: ItemPlan, b: ItemPlan): number {
  const rank = urgencyRank(a) - urgencyRank(b);
  if (rank !== 0) return rank;
  const aDate = a.orders[0]?.idealOrderDate ?? a.runoutDate ?? '9999';
  const bDate = b.orders[0]?.idealOrderDate ?? b.runoutDate ?? '9999';
  return aDate.localeCompare(bDate) || a.item.name.localeCompare(b.item.name);
}

const GAP_RANK = { blocking: 0, warning: 1, info: 2 } as const;
function compareGaps(a: DataGap, b: DataGap): number {
  return GAP_RANK[a.severity] - GAP_RANK[b.severity] || a.title.localeCompare(b.title);
}

function pickSupplier(list: PlanningSupplier[]): PlanningSupplier | null {
  if (list.length === 0) return null;
  return (
    list.find((s) => s.isPrimary) ??
    [...list].sort((a, b) => (a.leadTimeDays ?? 1e9) - (b.leadTimeDays ?? 1e9) || a.vendorName.localeCompare(b.vendorName))[0]
  );
}

function groupBy<T>(rows: T[], key: (row: T) => string): Map<string, T[]> {
  const out = new Map<string, T[]>();
  for (const row of rows) {
    const k = key(row);
    const list = out.get(k);
    if (list) list.push(row);
    else out.set(k, [row]);
  }
  return out;
}

function sum(values: number[]): number {
  let total = 0;
  for (const v of values) total += v;
  return total;
}

export function fmtQty(value: number): string {
  if (Math.abs(value - Math.round(value)) < 0.05) return String(Math.round(value));
  return value.toFixed(1);
}
